import React from 'react';
import { tokens } from '../theme/tokens';

export const BRAND_NAME = 'Flotillo';
export const BRAND_TAGLINE = 'Where float meets foresight.';

type Variant = 'onDark' | 'onLight';

// The Flotillo symbol (viewBox 96×64, 3:2). Logo-only colours — never used for UI chrome.
//   onDark  → light tile, navy bars (sits on the navy nav)
//   onLight → navy tile, light bars (sits on white / light backgrounds; also the favicon)
// Under 24px wide the dotted connector is dropped; bars, outline and diamond stay.
export const LogoMark: React.FC<{ variant?: Variant; height?: number; className?: string; title?: string; em?: number }> = ({
  variant = 'onLight', height = 24, className, title, em,
}) => {
  // em: size relative to the parent's font-size (fluid mode); the mark is always ≥ 24px
  // wide there, so it keeps the dotted connector.
  const width = em ? 99 : height * 1.5;
  const tile = variant === 'onDark' ? tokens.logoLight : tokens.logoNavy;
  const bars = variant === 'onDark' ? tokens.logoNavy : tokens.logoLight;
  const red = tokens.logoRed;
  return (
    <svg
      viewBox="0 0 96 64" width={em ? undefined : width} height={em ? undefined : height} fill="none" className={className}
      role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}
      style={em ? { flexShrink: 0, height: `${em}em`, width: `${em * 1.5}em` } : { flexShrink: 0 }}
    >
      <rect x="4" y="2" width="88" height="60" rx="16" fill={tile} />
      <rect x="12" y="12" width="32" height="8" rx="4" fill={bars} />
      <rect x="24" y="26" width="32" height="8" rx="4" fill={bars} />
      <rect x="37.5" y="41.5" width="19" height="6" rx="3" stroke={red} strokeWidth="3" />
      {width >= 24 && (
        <path d="M63 44.5H69" stroke={red} strokeWidth="3" strokeLinecap="round" strokeDasharray="0.1 5" />
      )}
      <path d="M80 35.5L88.5 44.5L80 53.5L71.5 44.5Z" fill={red} />
    </svg>
  );
};

// Symbol + "Flotillo" wordmark (Outfit 600, tracking -0.02em), 10px gap.
// wordmarkSize: a number (fixed px) or a CSS size such as a clamp() for fluid sizing —
// in fluid mode the mark is sized in em so it scales with the wordmark (57×38 at 34px).
export const Logo: React.FC<{
  variant?: Variant;
  wordmarkSize?: number | string;
  markHeight?: number;
  className?: string;
}> = ({ variant = 'onLight', wordmarkSize = 19, markHeight, className }) => {
  const fluid = typeof wordmarkSize === 'string';
  return (
    <span className={`inline-flex items-center ${className ?? ''}`} style={{ gap: 10, fontSize: wordmarkSize, lineHeight: 1 }}>
      {fluid
        ? <LogoMark variant={variant} em={38 / 34} />
        : <LogoMark variant={variant} height={markHeight ?? Math.round((wordmarkSize as number) * 1.25)} />}
      <span className="wordmark" style={{ color: variant === 'onDark' ? tokens.surface : tokens.ink }}>
        {BRAND_NAME}
      </span>
    </span>
  );
};

export default Logo;
